/**
 * ダッシュボードUIの多言語化(日本語 / 繁体字中国語)。
 *
 * 欣葉は台湾料理店で、現場スタッフ・料理長は中国語話者。SOP本文の翻訳は
 * エンジン側(translate コマンド → sop.zh.html)が担うが、それを開くまでの
 * 画面(品目一覧・評価・レビュー等)が日本語だけでは辿り着けない。
 *
 * 方式は src/core/i18n.ts(UI固定文言は静的テーブル)と同じ考え方:
 * - **原文(日本語)がキー。** コード中の文言はこれまでどおり日本語で書き、
 *   t() が言語設定に応じて i18n.zh.ts の辞書を引く。辞書に無い文は日本語のまま
 *   出す(隠れて欠落するより、日本語が見えて足りないと分かる方がよい)
 * - 動的な値は {name} プレースホルダで渡す(文字列連結で文を組むと語順の違う
 *   言語に訳せない)
 * - 選んだ言語は localStorage に保存(端末ごと)。既定は日本語
 *
 * 辞書の網羅は tests 側(web/src/lib/i18n.test.ts)が検査する。
 * サーバー(Functions)からのエラーメッセージは対象外(現状日本語のみ)。
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { LANG_STORAGE_KEY, localeOf, translate, type UiLang } from "./i18n-core";

export { dishTitle, localeOf, translate, type UiLang } from "./i18n-core";

export interface I18n {
  lang: UiLang;
  /** Date.toLocaleString 用。言語と揃える */
  locale: string;
  setLang(lang: UiLang): void;
  t(ja: string, params?: Record<string, string | number>): string;
}

const Ctx = createContext<I18n | null>(null);

export function useI18n(): I18n {
  const v = useContext(Ctx);
  if (!v) throw new Error("useI18n は I18nProvider の内側で使ってください");
  return v;
}

function storedLang(): UiLang {
  // プライベートモード等で localStorage が投げても画面は出す
  try {
    return window.localStorage.getItem(LANG_STORAGE_KEY) === "zh" ? "zh" : "ja";
  } catch {
    return "ja";
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<UiLang>(storedLang);

  useEffect(() => {
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, lang);
    } catch {
      /* 保存できなくても表示はできる */
    }
    document.documentElement.lang = lang === "zh" ? "zh-Hant" : "ja";
  }, [lang]);

  const value: I18n = {
    lang,
    locale: localeOf(lang),
    setLang,
    t: (ja, params) => translate(lang, ja, params),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** 言語切り替え。ナビに置く(ログイン画面は Login 側で個別に置く) */
export function LangSwitch() {
  const { lang, setLang } = useI18n();
  return (
    <span className="lang-switch" role="group" aria-label="Language">
      <button
        className={`link ${lang === "ja" ? "active" : ""}`}
        onClick={() => setLang("ja")}
        lang="ja"
      >
        日本語
      </button>
      <span className="muted">|</span>
      <button
        className={`link ${lang === "zh" ? "active" : ""}`}
        onClick={() => setLang("zh")}
        lang="zh-Hant"
      >
        中文
      </button>
    </span>
  );
}
